import axios from 'axios';
import { Collection, Note, UserInfo } from '@/types';

// API Configuration
const API_BASE = process.env.NODE_ENV === 'development'
  ? 'http://localhost:9099'
  : 'https://api.hintword.com';

const WS_BASE = process.env.NODE_ENV === 'development'
  ? 'ws://localhost:9099'
  : 'wss://api.hintword.com';

const axiosInstance = axios.create({
  baseURL: API_BASE,
});

axiosInstance.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('hintword_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Types
interface GoogleAuthResponse {
  data: {
    token: string;
  };
  status: number;
}

interface ApiResponse<T> {
  data: T;
  status: number;
  error?: string;
}

// WebSocket utility for note create/update
function sendNoteSync(payload: any): Promise<Note> {
  return new Promise((resolve, reject) => {
    const token = localStorage.getItem('hintword_token');
    if (!token) {
      reject(new Error('No authentication token found'));
      return;
    }

    // Pass token as query param
    const ws = new WebSocket(`${WS_BASE}/ws/note-sync?token=${encodeURIComponent(token)}`);

    ws.onopen = () => {
      ws.send(JSON.stringify({ payload }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.note) {
          ws.close();
          resolve(data.note);
        } else {
          reject(new Error('Invalid response from note-sync'));
        }
      } catch (err) {
        reject(err);
      }
    };

    ws.onerror = (err) => {
      reject(err);
    };
  });
}

// Notes REST API for create/update
async function createOrUpdateNote(noteData: Partial<Note>): Promise<Note> {
  const token = localStorage.getItem('hintword_token');
  if (!token) throw new Error('No authentication token found');
  const response = await axiosInstance.post('/v1/note/create', noteData, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  const data = response.data;
  if (data.status === -1) {
    throw new Error(data.error || 'Failed to create/update note');
  }
  return data.data;
}

// Grammar check API
async function checkGrammar({ html, noteId }: { html: string, noteId: string }) {
  const token = localStorage.getItem('hintword_token');
  if (!token) throw new Error('No authentication token found');
  const response = await axiosInstance.post('/v1/agent/completion', {
    context: 'grammar',
    note: html,
    note_id: noteId,
  }, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  const data = response.data;
  if (data?.data?.message) {
    return data.data.message; // corrected HTML
  }
  throw new Error('Grammar correction failed');
}

// API Functions
export const api = {
  // Auth
  googleAuth: async (userPayload: UserInfo): Promise<GoogleAuthResponse> => {

    const response = await fetch(`${API_BASE}/v1/auth/google/user-info`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userPayload),
    });

    if (!response.ok) {
      throw new Error('Failed to authenticate with backend');
    }

    return response.json();
  },

  // Collections
  getCollections: async (): Promise<ApiResponse<Collection[]>> => {
    const token = localStorage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.get('/v1/tab/collection', {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const data = response.data;
    if (data.status === -1) {
      throw new Error(data.error || 'Failed to fetch collections');
    }
    return { ...data, data: data.collection };
  },

  createCollection: async (collectionData: { name: string }): Promise<ApiResponse<Collection>> => {
    const token = localStorage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.post('/v1/tab/collection', {
      ...collectionData,
      status: 'ACTIVE',
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    const data = response.data;
    if (data.status === -1) {
      throw new Error(data.error || 'Failed to create collection');
    }
    return { ...data, data: Array.isArray(data.collection) ? data.collection[0] : data.collection };
  },

  createOrUpdateTab: async (tabData: any): Promise<ApiResponse<any>> => {
    const token = localStorage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.post('/v1/tab/create', tabData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    const data = response.data;
    if (data.status === -1) {
      throw new Error(data.error || 'Failed to create or update tab');
    }
    return data;
  },

  getNotes: async (): Promise<any> => {
    const token = localStorage.getItem('hintword_token');
    if (!token) throw new Error('No authentication token found');
    const response = await axiosInstance.get('/v1/note/list', {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const data = response.data;
    if (data.status === -1) {
      throw new Error(data.error || 'Failed to fetch notes');
    }
    return data.notes;
  },

  // Note sync via WebSocket
  sendNoteSync,
  // Note REST create/update
  createOrUpdateNote,
  checkGrammar,
}; 